import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { filter, interval, Subject, Subscription, takeUntil } from 'rxjs';
import { ServiceWorkerService } from '@/app/core/services/serviceworker.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'app-update-notification',
    standalone: true,
    imports: [CommonModule, ButtonModule, TranslateModule],
    templateUrl: './update-notification.component.html',
    styleUrl: './update-notification.component.scss',
})
export class UpdateNotificationComponent implements OnInit, OnDestroy {
    showUpdateNotification = false;
    countdown = 20;
    private readonly destroy$ = new Subject<void>();
    private countdownSub?: Subscription;
    private remindTimer?: ReturnType<typeof setTimeout>;
    private readonly onSwUpdate = (): void => {
        this.showUpdateNotification = true;
        this.startCountdown();
    };

    constructor(
        private messageService: MessageService,
        private serviceWorkerService: ServiceWorkerService,
    ) {}

    ngOnInit(): void {
        this.messageService.messageObserver
            .pipe(
                takeUntil(this.destroy$),
                filter((msg) => this.isUpdateAvailableMessage(msg)),
            )
            .subscribe(() => {
                this.showUpdateNotification = true;
                this.startCountdown();
            });

        window.addEventListener('sw-update-available', this.onSwUpdate);
    }

    private isUpdateAvailableMessage(msg: unknown): boolean {
        if (msg == null) {
            return false;
        }
        if (Array.isArray(msg)) {
            return msg.some((m) => m?.key === 'update-available');
        }
        return (msg as { key?: string }).key === 'update-available';
    }

    private startCountdown(): void {
        this.countdownSub?.unsubscribe();
        this.countdown = 20;
        this.countdownSub = interval(1000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.countdown > 1) {
                    this.countdown--;
                } else {
                    this.countdownSub?.unsubscribe();
                    this.updateNow();
                }
            });
    }

    ngOnDestroy(): void {
        if (this.remindTimer) {
            clearTimeout(this.remindTimer);
        }
        this.countdownSub?.unsubscribe();
        window.removeEventListener('sw-update-available', this.onSwUpdate);
        this.destroy$.next();
        this.destroy$.complete();
    }

    updateNow(): void {
        this.countdownSub?.unsubscribe();
        this.showUpdateNotification = false;
        this.messageService.clear('update-available');
        void this.serviceWorkerService.refresh();
    }

    dismissNotification(): void {
        this.countdownSub?.unsubscribe();
        this.showUpdateNotification = false;
        this.messageService.clear('update-available');
        this.remindUserLater(60);
    }

    private remindUserLater(seconds: number): void {
        this.remindTimer = setTimeout(() => {
            this.showUpdateNotification = true;
            this.startCountdown();
        }, seconds * 1000);
    }
}
